import {
  GoogleMap,
  Libraries,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';
import { useState, useCallback, useMemo, useEffect } from 'react'; // 🆕 import useEffect
import { ModalCarInfoComponent } from './modalCar';

import iconMarkerUser from '../../../../../assets/map/map_pointer_user.svg';
import imageCenterMap from '../../../../../assets/map/new_maps_center_icon.svg';

import iconMarkerGreen from '../../../../../assets/map/map_pointer_green.svg';
import iconMarkerBlue from '../../../../../assets/map/map_pointer_blue.svg';
import iconMarkerGray from '../../../../../assets/map/map_pointer_gray.svg';

import iconMarkerCarGreen from '../../../../../assets/map/map_pointer_car_green.svg';
import iconMarkerCarYellow from '../../../../../assets/map/map_pointer_car_yellow.svg';
import iconMarkerCarRed from '../../../../../assets/map/map_pointer_car_red.svg';
import { useLocationContext } from '../../../../../context/LocationContext';

import './index.css';
import { ModalConnectorInfoComponent } from './modalConnector';
import api from '../../../../../axios/axiosConfig';

const containerStyle = {
  width: '100%',
  height: '400px',
};

const libraries: Libraries = ['geometry'];

interface MapViewerComponentProps {
  brandColor: string;
  mapData: any;
}

export default function MapViewerComponent({
  mapData,
  brandColor,
}: MapViewerComponentProps) {
  const locationContext = useLocationContext();
  const [activeCs, setActiveCs] = useState<any>();
  const [isModalCarOpen, setIsModalCarOpen] = useState<boolean>(false);
  const [isModalConnectorOpen, setIsModalConnectorOpen] =
    useState<boolean>(false);

  const center = useMemo(
    () => locationContext.userLocation,
    [locationContext.userLocation]
  );

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    mapIds: [process.env.REACT_APP_GOOGLE_MAPS_CUSTOM_MAP_ID!],
    libraries: libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const centerMap = useCallback(() => {
    if (!map || !locationContext.userLocation) return;
    map.setZoom(15);
    map.panTo(locationContext.userLocation);
  }, [locationContext.userLocation, map]);

  const getMarkerUserIcon = useCallback(() => iconMarkerUser, []);

  const getMarkerIcon = useCallback((site: any) => {
    if (site?.chargerStatus?.available > 0) return iconMarkerGreen;
    else if (site?.chargerStatus?.inUse > 0) return iconMarkerBlue;
    else return iconMarkerGray;
  }, []);

  const getMarkerCarIcon = useCallback((telemetrySoc: number) => {
    if (telemetrySoc < 45) return iconMarkerCarRed;
    else if (telemetrySoc >= 45 && telemetrySoc < 65)
      return iconMarkerCarYellow;
    else return iconMarkerCarGreen;
  }, []);

  const handleClickMarkerCarPreview = useCallback((site: any) => {
    setActiveCs(site);
    setIsModalCarOpen(true);
    setIsModalConnectorOpen(false);
  }, []);

  const handleClickMarkerConnectorPreview = useCallback(async (site: any) => {
    setActiveCs(site);
    setIsModalCarOpen(false);
    setIsModalConnectorOpen(true);
    const { data }: any = await api.get(`ChargerSites/${site.csId}`);
    setActiveCs(data);
  }, []);

  const mapOptions = {
    streetViewControl: false,
    scaleControl: false,
    mapTypeControl: false,
    panControl: false,
    zoomControl: false,
    rotateControl: false,
    fullscreenControl: false,
    styles: [
      {
        featureType: 'poi',
        elementType: 'all',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit',
        elementType: 'all',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'road',
        elementType: 'all',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'landscape',
        elementType: 'all',
        stylers: [{ visibility: 'on' }],
      },
      {
        featureType: 'administrative',
        elementType: 'all',
        stylers: [{ visibility: 'on' }],
      },
    ],
  };

  // 🆕 Efeito para centralizar o mapa automaticamente
  useEffect(() => {
    if (!map) return;

    const bounds = new window.google.maps.LatLngBounds();

    // adiciona o usuário
    if (center?.lat && center?.lng) {
      bounds.extend(center);
    }

    // adiciona veículos
    mapData?.vehicles?.forEach((point: any) => {
      const coords = point?.uvTelemetryLocation?.coordinates;
      if (coords) {
        bounds.extend({ lat: coords[1], lng: coords[0] });
      }
    });

    // adiciona conectores
    mapData?.connectors?.forEach((point: any) => {
      const coords = point?.csLocation?.coordinates;
      if (coords) {
        bounds.extend({ lat: coords[1], lng: coords[0] });
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);

      // limita o zoom máximo (opcional)
      const listener = google.maps.event.addListenerOnce(
        map,
        'bounds_changed',
        () => {
          if (map.getZoom()! > 16) {
            map.setZoom(16);
          }
          google.maps.event.removeListener(listener);
        }
      );
    }
  }, [mapData, center, map]); // 🆕 depende dos dados e da localização

  return (
    <>
      {isLoaded && (
        <div style={{ position: 'relative' }}>
          <button
            className='map-center-button'
            onClick={centerMap}
            style={{ right: 20, top: 20 }}
          >
            <img
              className='map-center-button-image'
              src={imageCenterMap}
              alt='map-center'
            />
          </button>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={13}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions}
          >
            <Marker
              key='user-key'
              icon={getMarkerUserIcon()}
              position={center}
              zIndex={1}
            />
            {mapData.vehicles.map((point: any) =>
              point?.uvTelemetryLocation?.coordinates ? (
                <Marker
                  key={point.vmId + crypto.randomUUID()}
                  icon={getMarkerCarIcon(point.uvTelemetrySoc)}
                  position={{
                    lat: point.uvTelemetryLocation.coordinates[1],
                    lng: point.uvTelemetryLocation.coordinates[0],
                  }}
                  onClick={() => handleClickMarkerCarPreview(point)}
                  zIndex={2}
                />
              ) : null
            )}
            {mapData.connectors.map((point: any) =>
              point?.csLocation?.coordinates ? (
                <Marker
                  key={point.csId + crypto.randomUUID()}
                  icon={getMarkerIcon(point)}
                  position={{
                    lat: point.csLocation.coordinates[1],
                    lng: point.csLocation.coordinates[0],
                  }}
                  onClick={() => handleClickMarkerConnectorPreview(point)}
                  zIndex={2}
                />
              ) : null
            )}
          </GoogleMap>
        </div>
      )}

      {/* legendas e modais permanecem iguais */}
      <div className='map-legend_container'>
        {/* ... seu código das legendas ... */}
      </div>

      {isModalCarOpen && (
        <ModalCarInfoComponent
          info={activeCs}
          brandColor={brandColor}
          isModalOpen={isModalCarOpen}
          setIsModalOpen={setIsModalCarOpen}
        />
      )}
      {isModalConnectorOpen && (
        <ModalConnectorInfoComponent
          info={activeCs}
          brandColor={brandColor}
          isModalOpen={isModalConnectorOpen}
          setIsModalOpen={setIsModalConnectorOpen}
        />
      )}
    </>
  );
}
